/** @jsx isolateComponent */
import { useState } from 'react';

import { isolateComponent } from '@zapier/style-encapsulation';

import NavDot, {
  ACTIVE,
  ENABLED,
  DISABLED,
  DOT_SIZE,
  DotState,
} from './NavDot';

type Props = {
  /** The index of the currently active dot. */
  activeDotIndex: number;
  /** Called when a dot is clicked. */
  onDotClick: (dotIndex: number) => void;
  /**
   * What tooltip should show when each dot is hovered / focused? The number
   * of tooltips provided determines how many NavDots are rendered.
   */
  tooltips: string[];
};

const Styles = {
  root: (totalDots: number) => ({
    display: 'grid',
    gridGap: '10px',
    gridTemplateColumns: `repeat(${totalDots}, ${DOT_SIZE})`,
  }),
};

export const pickState = (
  activeDotIndex: number,
  dotIndex: number
): DotState => {
  if (dotIndex === activeDotIndex) {
    return ACTIVE;
  }
  if (dotIndex < activeDotIndex) {
    return ENABLED;
  }
  return DISABLED;
};

/**
 * Renders a grouping of clickable nav dots in varying states.
 */
const NavDots = ({ activeDotIndex, onDotClick, tooltips }: Props) => {
  const totalDots = tooltips.length;

  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  if (activeDotIndex >= totalDots) {
    throw new Error(
      `NavDot ${
        activeDotIndex + 1
      } cannot be active because there are only ${totalDots} total dots.`
    );
  }

  return (
    <nav css={Styles.root(totalDots)}>
      {Array.from(Array(totalDots), (_, i) => {
        return (
          <NavDot
            key={i}
            number={i + 1}
            onBlur={() => setFocusIndex(null)}
            onClick={() => {
              setFocusIndex(null);
              onDotClick(i);
            }}
            onFocus={() => setFocusIndex(i)}
            showTooltip={focusIndex === i}
            state={pickState(activeDotIndex, i)}
            tooltip={tooltips[i]}
          />
        );
      })}
    </nav>
  );
};

export default NavDots;
